import { ArrowBackIos as ArrowBackIosIcon, ArrowForwardIos as ArrowForwardIosIcon } from "@mui/icons-material";
import { Box, IconButton, Stack } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./carousel.css";

/**
 * MUICarousel - Custom carousel component built with MUI primitives
 * Replaces react-material-ui-carousel with a lightweight, MUI-native implementation
 * Uses CSS transforms to slide items in/out similar to the original library
 *
 * @param {Object} props
 * @param {React.ReactNode[]} props.children - Child elements (carousel items)
 * @param {number} props.interval - Auto-play interval in milliseconds (default: 5000)
 * @param {boolean} props.autoplay - Enable auto-play (default: true)
 * @param {React.ReactNode} props.NextIcon - Icon for next button
 * @param {React.ReactNode} props.PrevIcon - Icon for previous button
 * @param {string} props.className - CSS class name for the carousel
 * @param {boolean} props.navButtonsAlwaysVisible - Show navigation buttons always
 * @param {boolean} props.swipe - Enable swipe gestures (default: true)
 * @param {number} props.duration - Animation duration in milliseconds (default: 500)
 * @returns {JSX.Element} Carousel component
 */
const MUICarousel = React.forwardRef(
	(
		{
			children,
			interval = 5000,
			autoplay = true,
			NextIcon = <ArrowForwardIosIcon color="white" />,
			PrevIcon = <ArrowBackIosIcon color="white" />,
			className = "",
			navButtonsAlwaysVisible = false,
			swipe = true,
			duration = 500,
		},
		ref,
	) => {
		const [currentIndex, setCurrentIndex] = useState(0);
		const [isAutoplayActive, setIsAutoplayActive] = useState(autoplay);
		const [touchStart, setTouchStart] = useState(null);
		const [isTransitioning, setIsTransitioning] = useState(false);
		const isTransitioningRef = useRef(isTransitioning);
		const itemCount = React.Children.count(children);
		const items = React.Children.toArray(children);

		useEffect(() => {
			isTransitioningRef.current = isTransitioning;
		}, [isTransitioning]);

		// Navigate to next slide
		const goToNext = useCallback(() => {
			if (isTransitioningRef.current) return;
			setIsTransitioning(true);
			setCurrentIndex((prevIndex) => (prevIndex + 1) % itemCount);
			setTimeout(() => setIsTransitioning(false), duration);
		}, [itemCount, duration]);

		// Navigate to previous slide
		const goToPrev = useCallback(() => {
			if (isTransitioningRef.current) return;
			setIsTransitioning(true);
			setCurrentIndex((prevIndex) => (prevIndex - 1 + itemCount) % itemCount);
			setTimeout(() => setIsTransitioning(false), duration);
		}, [itemCount, duration]);

		// Auto-play effect
		useEffect(() => {
			if (!isAutoplayActive || !autoplay) {
				return;
			}

			const timer = setInterval(goToNext, interval);

			return () => clearInterval(timer);
		}, [isAutoplayActive, autoplay, interval, goToNext]);

		const handleTouchStart = (e) => {
			setTouchStart(e.targetTouches[0].clientX);
		};

		const handleTouchEnd = (e) => {
			if (swipe && touchStart !== null && e.changedTouches[0].clientX !== null) {
				const touchEndX = e.changedTouches[0].clientX;
				const distance = touchStart - touchEndX;
				if (distance > 50) {
					goToNext();
				} else if (distance < -50) {
					goToPrev();
				}
			}

			setTouchStart(null);
		};

		const handleMouseEnter = () => {
			setIsAutoplayActive(false);
		};

		const handleMouseLeave = () => {
			setIsAutoplayActive(autoplay);
		};

		// Get transform percentage for each item based on position relative to current
		const getTransform = (index) => {
			if (index === currentIndex) {
				return "translateX(0%)";
			}

			const offset = index < currentIndex ? -1 : 1;
			const diff = index - currentIndex;
			if (Math.abs(diff) === itemCount - 1) {
				// Wrap around case
				return offset === 1 ? "translateX(100%)" : "translateX(-100%)";
			}

			return offset > 0 ? "translateX(100%)" : "translateX(-100%)";
		};

		const transitionDurationSec = duration / 1000;
		const getItemKey = (item, index) => {
			if (item && typeof item === "object" && "key" in item && item.key != null) {
				return item.key;
			}
			return `carousel-item-${index}`;
		};

		return (
			<Box
				ref={ref}
				className={`mui-carousel ${className}`}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
				sx={{
					position: "relative",
					width: "100%",
					overflow: "hidden",
				}}
			>
				{/* Carousel Items Container */}
				<Box
					sx={{
						position: "relative",
						width: "100%",
						height: "100%",
					}}
				>
					{items.map((item, index) => (
						<Box
							key={getItemKey(item, index)}
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								transform: getTransform(index),
								transition: isTransitioning
									? `transform ${transitionDurationSec}s ease-in-out`
									: "none",
								opacity: index === currentIndex ? 1 : 0.5,
								zIndex: index === currentIndex ? 1 : 0,
								"@media (max-width: 750px)": {
									opacity: index === currentIndex ? 1 : 0,
								},
							}}
						>
							{item}
						</Box>
					))}
				</Box>

				{/* Navigation Buttons */}
				<Stack
					direction="row"
					sx={{
						position: "absolute",
						top: 0,
						width: "100%",
						height: "100%",
						justifyContent: "space-between",
						alignItems: "center",
						pointerEvents: "none",
						zIndex: 10,
					}}
				>
					{/* Previous Button */}
					<IconButton
						onClick={goToPrev}
						disabled={isTransitioning}
						sx={{
							pointerEvents: "auto",
							color: "white",
							fontSize: "20px",
							opacity: navButtonsAlwaysVisible ? 0.9 : 0,
							transition: "opacity 0.3s ease",
							"&:hover": {
								opacity: 0.9,
								backgroundColor: "rgba(0, 0, 0, 0.5)",
							},
							"&:disabled": {
								opacity: navButtonsAlwaysVisible ? 0.5 : 0,
							},
						}}
						aria-label="Previous slide"
					>
						{PrevIcon}
					</IconButton>

					{/* Next Button */}
					<IconButton
						onClick={goToNext}
						disabled={isTransitioning}
						sx={{
							pointerEvents: "auto",
							color: "white",
							fontSize: "20px",
							opacity: navButtonsAlwaysVisible ? 0.9 : 0,
							transition: "opacity 0.3s ease",
							"&:hover": {
								opacity: 0.9,
								backgroundColor: "rgba(0, 0, 0, 0.5)",
							},
							"&:disabled": {
								opacity: navButtonsAlwaysVisible ? 0.5 : 0,
							},
						}}
						aria-label="Next slide"
					>
						{NextIcon}
					</IconButton>
				</Stack>
			</Box>
		);
	},
);

MUICarousel.displayName = "MUICarousel";

export default MUICarousel;
